import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileCheck2, Lock, FlaskConical, ClipboardCheck, UserCheck, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FeaturedProductCard } from "@/components/FeaturedProductCard";
import { getPrefetched } from "@/lib/api";

// Same-origin assets: served by whichever deployment renders the page (no third-party image hosts on the critical path).
// Built by backend/build_web_images.py - responsive AVIF/WebP sets so phones download ~25 KB instead of ~160 KB.
const BRAND = "/api/static/brand";
const HERO_WIDTHS = [640, 768, 960, 1280, 1600];
const heroSet = (fmt) => HERO_WIDTHS.map((w) => `${BRAND}/hero-vial-${w}.${fmt} ${w}w`).join(", ");
const HERO_SIZES = "(min-width: 1024px) 720px, (min-width: 640px) 90vw, 100vw";
const HERO_VIAL_FALLBACK = `${BRAND}/hero-vial-1280.webp`;
const backdropSet = (fmt) => `${BRAND}/hero-backdrop-720.${fmt} 720w, ${BRAND}/hero-backdrop-1280.${fmt} 1280w`;

export default function Home() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    getPrefetched("products", "/public/products").then(({ data }) => setProducts((data.products || []).slice(0, 8))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="dark-section relative overflow-hidden pg-noise">
        <div className="absolute inset-0">
          <picture>
            <source srcSet={backdropSet("avif")} type="image/avif" sizes="100vw" />
            <source srcSet={backdropSet("webp")} type="image/webp" sizes="100vw" />
            <img src={`${BRAND}/hero-backdrop-1280.webp`} alt="" aria-hidden="true" width={1280} height={720} decoding="async" className="h-full w-full object-cover opacity-20" />
          </picture>
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(8,21,37,0.96) 0%, rgba(11,28,51,0.92) 45%, rgba(53,208,223,0.14) 100%)" }} />
        </div>
        {/* Desktop: hero fills the first screen minus the header (73px) and a 120px white band; the About section's
            top padding (128px) is larger than that band, so its text always starts below the fold instead of being cut. */}
        <div className="pg-container relative px-4 py-8 sm:px-6 sm:py-16 lg:flex lg:min-h-[calc(100svh-193px)] lg:max-w-[1320px] lg:items-center lg:px-8 lg:py-12">
          <div className="grid w-full grid-cols-1 items-center gap-8 sm:gap-12 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5 animate-in fade-in slide-in-from-bottom-3 duration-500 motion-reduce:animate-none">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-brand-cyan">
                <ShieldCheck className="h-3.5 w-3.5" /> Secure members-only platform
              </span>
              <h1 data-testid="hero-title" className="mt-5 font-heading text-4xl font-bold leading-[1.1] tracking-tight text-brand-cyan sm:text-5xl">
                Precision-Focused Scientific Solutions
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300">
                An independent scientific brand built on documentation-first transparency and strict
                regulatory publication controls. Explore our catalogue of permitted products and apply for secure
                member access.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button data-testid="home-hero-explore-button" size="lg" onClick={() => navigate("/products")} className="bg-brand-blue text-white hover:bg-[#2F6DE0]">
                  Explore Our Products <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button data-testid="home-hero-apply-button" size="lg" variant="outline" onClick={() => navigate("/membership")} className="border-white/25 bg-transparent text-white hover:bg-white/10">
                  Apply for Membership
                </Button>
              </div>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-400">
                <span className="inline-flex items-center gap-2"><FileCheck2 className="h-4 w-4 text-brand-cyan" /> Documentation-first</span>
                <span className="inline-flex items-center gap-2"><Lock className="h-4 w-4 text-brand-cyan" /> Server-secured pricing</span>
                <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-cyan" /> Regulatory controls</span>
              </div>
            </div>

            <div className="order-first lg:order-none lg:col-span-7 animate-in fade-in slide-in-from-bottom-4 duration-700 motion-reduce:animate-none">
              <div data-testid="hero-vial" className="rounded-3xl border border-white/10 bg-white/5 p-2 backdrop-blur-sm sm:p-2.5">
                <Link to="/products" className="group block overflow-hidden rounded-2xl" aria-label="PEPTIGENIE Retatrutide vial - view catalogue">
                  <picture>
                    <source srcSet={heroSet("avif")} sizes={HERO_SIZES} type="image/avif" />
                    <source srcSet={heroSet("webp")} sizes={HERO_SIZES} type="image/webp" />
                    <img
                      src={HERO_VIAL_FALLBACK}
                      alt="PEPTIGENIE Retatrutide 5 mg research vial"
                      width={1600}
                      height={1400}
                      fetchpriority="high"
                      decoding="async"
                      className="aspect-[4/3] w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.03] sm:aspect-[8/7] lg:aspect-[7/6]"
                    />
                  </picture>
                </Link>
                <div className="hidden flex-col gap-3 px-3 pb-2 pt-3 sm:flex sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-heading text-sm font-semibold text-white">PEPTIGENIE Series</p>
                    <p className="text-xs text-slate-400">36 research-grade products, one consistent standard</p>
                  </div>
                  <Button data-testid="hero-view-catalogue-button" onClick={() => navigate("/products")} className="bg-brand-cyan text-navy hover:bg-[#5FE0EA]">
                    View catalogue <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="pg-container px-4 py-16 sm:px-6 lg:px-8 lg:pb-24 lg:pt-32">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-blue">About PEPTIGENIE</p>
            <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-navy sm:text-4xl">A scientific brand engineered around trust</h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base leading-relaxed text-muted-foreground">
              PEPTIGENIE is an independent scientific brand presenting a curated catalogue under a single, clear
              identity. We prioritise accurate product information, careful regulatory classification, and a secure
              access model. Product details are published only where legally permitted, and pricing is reserved for
              approved members behind a server-enforced access layer.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { icon: FlaskConical, title: "Curated catalogue", text: "Permitted products only, clearly specified." },
                { icon: FileCheck2, title: "Documentation", text: "Specifications and verified documents." },
                { icon: Lock, title: "Private pricing", text: "Member pricing secured on the server." },
              ].map((c) => (
                <div key={c.title} className="rounded-2xl border border-border bg-white p-5 shadow-[0_1px_2px_rgba(8,21,37,0.06)]">
                  <c.icon className="h-6 w-6 text-brand-blue" />
                  <h3 className="mt-3 font-heading text-sm font-semibold text-navy">{c.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{c.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-slate-50 py-16 lg:py-24">
        <div className="pg-container px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-blue">Catalogue</p>
              <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-navy sm:text-4xl">Products</h2>
              <p className="mt-2 max-w-xl text-sm text-muted-foreground">A selection from the PEPTIGENIE series. Member pricing is available to approved members after login.</p>
            </div>
          </div>
          {products.length > 0 ? (
            <div data-testid="featured-products-grid" className="mt-8 grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
              {products.map((p) => <FeaturedProductCard key={p.id} product={p} />)}
            </div>
          ) : (
            <div className="mt-8 rounded-2xl border border-dashed border-border bg-white p-10 text-center">
              <FlaskConical className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm text-muted-foreground">Our public catalogue is being curated. Products appear here only once reviewed and approved for public listing.</p>
            </div>
          )}
          <div className="mt-10 text-center">
            <Button data-testid="home-view-full-catalogue-button" size="lg" onClick={() => navigate("/products")} className="bg-brand-blue text-white hover:bg-[#2F6DE0]">
              View full catalogue <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* HOW MEMBERSHIP WORKS */}
      <section className="pg-container px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-blue">Membership</p>
          <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-navy sm:text-4xl">How access works</h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">Membership is granted by manual review. Registering does not provide instant pricing access.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: ClipboardCheck, step: "01", title: "Apply", text: "Submit your membership application with business details." },
            { icon: UserCheck, step: "02", title: "Review", text: "Our team manually reviews every application." },
            { icon: KeyRound, step: "03", title: "Activate", text: "Approved applicants set their own secure password." },
            { icon: Lock, step: "04", title: "Access", text: "Log in with 2FA and view authorised member pricing." },
          ].map((s) => (
            <div key={s.step} className="relative rounded-2xl border border-border bg-white p-6 shadow-[0_1px_2px_rgba(8,21,37,0.06)]">
              <span className="font-mono text-xs font-semibold text-brand-cyan">{s.step}</span>
              <s.icon className="mt-3 h-7 w-7 text-brand-blue" />
              <h3 className="mt-3 font-heading text-base font-semibold text-navy">{s.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Button size="lg" onClick={() => navigate("/membership")} className="bg-navy text-white hover:bg-navy/90">Apply for Membership</Button>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="bg-slate-50 py-16">
        <div className="pg-container px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-6 rounded-3xl border border-border bg-white p-10 text-center shadow-sm sm:flex-row sm:text-left">
            <div>
              <h3 className="font-heading text-2xl font-semibold text-navy">Have a question?</h3>
              <p className="mt-2 text-muted-foreground">Contact the PEPTIGENIE team for enquiries about products, documentation or membership.</p>
            </div>
            <Button size="lg" onClick={() => navigate("/contact")} className="bg-brand-blue text-white hover:bg-[#2F6DE0]">Contact PEPTIGENIE</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
